import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import DOMPurify from 'dompurify';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter } from '@/components/SiteFooter';
import { usePageTitle } from '@/hooks/usePageTitle';

/**
 * Reusable legal page shell.
 * Once you have your Termly embed IDs, replace the `termlyId` prop
 * value in each route and the Termly script will auto-embed.
 */
export default function LegalPage({ title, termlyId, placeholder }) {
  usePageTitle(title);
  const embedRef = useRef(null);

  useEffect(() => {
    if (!termlyId || !embedRef.current) return;
    const node = embedRef.current;

    // Clear previous content safely
    while (node.firstChild) {
      node.removeChild(node.firstChild);
    }

    // Inject Termly embed div using DOM API (no innerHTML)
    const termlyDiv = document.createElement('div');
    termlyDiv.setAttribute('name', 'termly-embed');
    termlyDiv.setAttribute('data-id', DOMPurify.sanitize(termlyId));
    node.appendChild(termlyDiv);

    // Load Termly script
    const script = document.createElement('script');
    script.src = 'https://app.termly.io/embed-policy.min.js';
    script.async = true;
    node.appendChild(script);

    return () => {
      while (node.firstChild) {
        node.removeChild(node.firstChild);
      }
    };
  }, [termlyId]);

  return (
    <div className="min-h-screen">
      <SiteHeader />
      <section className="py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="font-['Inter'] text-xs font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))] mb-4">
              Legal
            </p>
            <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-bold text-[hsl(var(--foreground))] mb-10 leading-[1.1]">
              {title}
            </h1>

            {termlyId ? (
              <div
                ref={embedRef}
                className="reading-room font-['Inter'] text-[15px] leading-[1.8] text-[hsl(var(--muted-foreground))]"
              />
            ) : (
              <div className="rounded-xl bg-[hsl(var(--card))] border border-[rgba(201,168,76,0.15)] p-8 sm:p-12 text-center">
                <p className="font-['Inter'] text-base text-[hsl(var(--muted-foreground))] mb-2">
                  {placeholder || 'This policy is being prepared and will be available shortly.'}
                </p>
                <p className="font-['Inter'] text-sm text-[hsl(var(--silver))]">
                  For questions, please contact us at{' '}
                  <a
                    href="mailto:hello@thecosmicsynthesist.com"
                    className="text-[hsl(var(--gold))] hover:text-[hsl(var(--champagne))] transition-colors"
                  >
                    hello@thecosmicsynthesist.com
                  </a>
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
